import { RadioGroupInput, SelectInput, TextAreaInput } from 'components'
import {
  ASSUMED_INJURY_OPTIONS,
  INJURED_SUMMER_BODY_PART_OPTIONS,
  INJURY_FORM_ORIENTATION_OPTIONS,
  INJURY_FORM_SIDE_OPTIONS
} from 'constants/options'
import { t } from 'i18n'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AssumedInjuryV2 } from 'store/ducks/report/types'
import {
  getAllowedInjuryTypes,
  InjuryType,
  isInjuryAllowedForBodyPart
} from './bodyPartInjuryMapping'
import { requiresOrientation, requiresSide } from './bodyPartSVGMapping'

interface InjuryFormProps {
  injury: AssumedInjuryV2
  onChange: (injury: AssumedInjuryV2) => void
  namePrefix?: string
}

export const InjuryForm: React.FC<InjuryFormProps> = ({
  injury,
  injury: { assumedInjury, injuredBodyPart: bodyPart },
  onChange,
  namePrefix = 'injury'
}) => {
  type ConditionType = 'dead' | 'acuteSickness'

  const [mode, setMode] = useState<'injury' | 'condition'>(() =>
    assumedInjury === 'dead' || assumedInjury === 'acuteSickness'
      ? 'condition'
      : 'injury'
  )

  const updateInjury = useCallback(
    (patch: Partial<AssumedInjuryV2>) => onChange({ ...injury, ...patch }),
    [injury, onChange]
  )

  const handleField = useCallback(
    (field: keyof AssumedInjuryV2, val: string | boolean) => {
      updateInjury({ [field]: val } as Partial<AssumedInjuryV2>)
    },
    [updateInjury]
  )

  // Filter injury options based on selected body part
  const filteredInjuryOptions = useMemo(() => {
    const allowedTypes = getAllowedInjuryTypes({
      bodyPart,
      mode
    })
    if (!allowedTypes.length) return []

    return ASSUMED_INJURY_OPTIONS.filter(option =>
      allowedTypes.includes(option.id as InjuryType)
    )
  }, [mode, bodyPart])

  // Clear injury selection if it's not allowed for the selected body part
  useEffect(() => {
    if (
      bodyPart &&
      assumedInjury &&
      !isInjuryAllowedForBodyPart(bodyPart, assumedInjury)
    ) {
      updateInjury({ assumedInjury: undefined })
    }
  }, [bodyPart, assumedInjury, updateInjury])

  return (
    <>
      <RadioGroupInput
        name={`${namePrefix}-mode`}
        value={mode}
        options={[
          { id: 'injury', label: 'injury' },
          { id: 'condition', label: 'condition' }
        ]}
        onChange={(_, v) => setMode(v === 'injury' ? 'injury' : 'condition')}
      />

      {mode === 'injury' && (
        <>
          <SelectInput
            name={`${namePrefix}-bodypart`}
            label="injuredBodypart"
            value={bodyPart || ''}
            onChange={e => handleField('injuredBodyPart', e.target.value)}
            options={INJURED_SUMMER_BODY_PART_OPTIONS}
            translateText={true}
          />

          <SelectInput
            name={`${namePrefix}-injury`}
            label="assumedInjury"
            value={
              // when switching modes, ensure the value is valid
              getAllowedInjuryTypes({
                bodyPart,
                mode
              }).includes(assumedInjury as InjuryType)
                ? assumedInjury
                : ''
            }
            onChange={e => handleField('assumedInjury', e.target.value)}
            options={filteredInjuryOptions}
            translateText={true}
          />

          {/* Conditionally show side selection */}
          {bodyPart && requiresSide(bodyPart) && (
            <RadioGroupInput
              name={`${namePrefix}-side`}
              label="side"
              value={injury.side || ''}
              options={INJURY_FORM_SIDE_OPTIONS}
              onChange={(_, v) => handleField('side', v)}
            />
          )}

          {/* Conditionally show orientation selection */}
          {bodyPart && requiresOrientation(bodyPart) && (
            <RadioGroupInput
              name={`${namePrefix}-orientation`}
              label="orientation"
              value={injury.orientation || ''}
              options={INJURY_FORM_ORIENTATION_OPTIONS}
              onChange={(_, v) => handleField('orientation', v)}
            />
          )}
        </>
      )}

      {mode === 'condition' && (
        <RadioGroupInput
          name={`${namePrefix}-condition`}
          label="selectCondition"
          value={assumedInjury && mode === 'condition' ? assumedInjury : ''}
          options={filteredInjuryOptions}
          onChange={(_, v) => {
            if (typeof v === 'string' && v) {
              updateInjury({
                assumedInjury: v as ConditionType,
                injuredBodyPart: undefined,
                side: undefined,
                orientation: undefined
              })
            }
          }}
        />
      )}

      <TextAreaInput
        name={`${namePrefix}-comments`}
        value={injury.comments || ''}
        label="comments"
        onChange={e => handleField('comments', e.target.value)}
        placeholder={t('clickHereToWriteMore')}
      />
    </>
  )
}
